import * as Dialog from '@radix-ui/react-dialog';
import type { SessionSummary } from '@diana/contracts';
import { ArrowUpRight } from 'lucide-react';
import { useId, useState, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Select,
  TextInput,
  useDianaUIPortal,
  useCollectionNavigation,
} from '../../ui/index.js';
import { CompactDialogContent } from '../../ui/CompactDialogContent.js';
import { ClientAvatar } from '../crm/ClientAvatar.js';
import { clientInitials } from '../crm/client-activity.js';
import { formatCrmMoney } from '../crm/crm-money.js';
import {
  dashboardFinance,
  dashboardOptions,
  lisbonDate,
  MONTHS,
  monthLabel,
  packageIdentity,
  periodRange,
  rangeError,
  PERIOD_PRESETS,
  type PeriodPreset,
} from './dashboard-data.js';
import './Dashboard.css';

type Detail = { title: string; kind?: 'Income' | 'Expense'; packageKey?: string };
export function DashboardFinance({
  shoots,
  today = lisbonDate(new Date()),
  demo = false,
  pageTitle,
  workSummary,
}: {
  shoots: readonly SessionSummary[];
  today?: string;
  demo?: boolean;
  pageTitle?: ReactNode;
  workSummary?: ReactNode;
}) {
  const options = dashboardOptions(shoots);
  const initialRange = periodRange('last12', today, { start: today, end: today });
  const { value: filters, setValue: setFilters } = useCollectionNavigation(
    demo ? 'ui-kit-dashboard-finance-v2' : 'crm-dashboard-finance-v2',
    () => ({
      preset: 'last12' as PeriodPreset,
      currency: options.currencies[0]!,
      month: null as string | null,
      start: initialRange.start,
      end: initialRange.end,
    }),
    { restoreWindow: false },
  );
  const currency = options.currencies.includes(filters.currency)
    ? filters.currency
    : options.currencies[0]!;
  const range = periodRange(filters.preset, today, { start: filters.start, end: filters.end });
  const error = rangeError(range);
  const month =
    filters.month &&
    filters.month >= range.start.slice(0, 7) &&
    filters.month <= range.end.slice(0, 7)
      ? filters.month
      : null;
  const setMonth = (month: string | null) => setFilters((current) => ({ ...current, month }));
  const data = dashboardFinance(shoots, error ? initialRange : range, currency, month);
  const period = month
    ? monthLabel(month)
    : `${monthLabel(range.start.slice(0, 7))} – ${monthLabel(range.end.slice(0, 7))}`;
  const modalRange = error ? initialRange : { ...range };
  if (month) {
    if (`${month}-01` > modalRange.start) modalRange.start = `${month}-01`;
    const lastDay = new Date(`${month}-01T00:00:00Z`);
    lastDay.setUTCMonth(lastDay.getUTCMonth() + 1, 0);
    const end = lastDay.toISOString().slice(0, 10);
    if (end < modalRange.end) modalRange.end = end;
  }
  const cashDate = (value: string) =>
    `${Number(value.slice(8, 10))} ${MONTHS[Number(value.slice(5, 7)) - 1]} ${value.slice(0, 4)}`;
  const modalPeriod =
    modalRange.start === modalRange.end
      ? cashDate(modalRange.start)
      : `${cashDate(modalRange.start)} – ${cashDate(modalRange.end)}`;
  const [detail, setDetail] = useState<Detail | null>(null);
  const portal = useDianaUIPortal();
  const inputId = useId();
  const records = data.records.filter(
    (r) =>
      (!detail?.kind || r.transaction.kind === detail.kind) &&
      (!detail?.packageKey || packageIdentity(r.shoot) === detail.packageKey),
  );
  const chartScroll = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const node = chartScroll.current;
    if (!node) return;
    const selected = node.querySelector<HTMLElement>('[aria-pressed="true"]');
    node.scrollLeft = selected
      ? selected.getBoundingClientRect().left -
        node.getBoundingClientRect().left +
        node.scrollLeft -
        node.clientWidth / 2 +
        selected.clientWidth / 2
      : node.scrollWidth - node.clientWidth;
  }, [range.start, range.end]);
  const max = Math.max(1, ...data.months.map((m) => m.income));
  const dates =
    filters.preset === 'custom' ? (
      <div className="crm-dashboard__custom-dates">
        <label htmlFor={`${inputId}-start`}>
          Start date
          <TextInput
            id={`${inputId}-start`}
            type="date"
            min="0001-01-01"
            max="9999-12-31"
            value={filters.start}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${inputId}-error` : undefined}
            onChange={(e) =>
              setFilters((current) => ({ ...current, start: e.target.value, month: null }))
            }
          />
        </label>
        <label htmlFor={`${inputId}-end`}>
          End date
          <TextInput
            id={`${inputId}-end`}
            type="date"
            min="0001-01-01"
            max="9999-12-31"
            value={filters.end}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${inputId}-error` : undefined}
            onChange={(e) =>
              setFilters((current) => ({ ...current, end: e.target.value, month: null }))
            }
          />
        </label>
      </div>
    ) : null;
  const moneySummary = (
    <section
      className="crm-dashboard__panel crm-dashboard__money-overview"
      aria-label="Money overview"
    >
      <h2>Money overview</h2>
      <div className="crm-dashboard__totals" aria-label="Money summary">
        {[
          { label: 'Earned', amount: data.net, kind: undefined },
          { label: 'Received', amount: data.income, kind: 'Income' as const },
          { label: 'Expenses', amount: data.expenses, kind: 'Expense' as const },
        ].map((metric, i) => (
          <button
            key={metric.label}
            className={`crm-dashboard__total${i === 0 ? ' is-primary' : ''}`}
            onClick={() =>
              setDetail({ title: metric.label, ...(metric.kind ? { kind: metric.kind } : {}) })
            }
          >
            <span>
              {metric.label}
              <ArrowUpRight size={15} aria-hidden="true" />
            </span>
            <strong data-negative={metric.amount < 0 || undefined}>
              {data.missing ? '—' : formatCrmMoney(metric.amount, currency)}
            </strong>
          </button>
        ))}
      </div>
    </section>
  );
  const heading = (
    <header
      className={`crm-dashboard__heading crm-dashboard__finance-heading${filters.preset === 'custom' ? ' has-custom' : ''}`}
    >
      {pageTitle ?? <h2>Dashboard overview</h2>}
      <div className="crm-dashboard__period">
        {dates}
        {month ? (
          <Button
            variant="outline"
            className="crm-dashboard__clear-month"
            onClick={() => setMonth(null)}
          >
            All months
          </Button>
        ) : null}
        <Select
          wrapperClassName="crm-dashboard__period-select"
          aria-label="Financial period"
          value={filters.preset}
          onChange={(e) =>
            setFilters((current) => ({
              ...current,
              preset: e.target.value as PeriodPreset,
              month: null,
              ...(e.target.value === 'custom' && current.preset !== 'custom' && !error
                ? { start: range.start, end: range.end }
                : {}),
            }))
          }
        >
          {PERIOD_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </Select>
        {options.currencies.length > 1 ? (
          <Select
            wrapperClassName="crm-dashboard__currency-select"
            aria-label="Financial currency"
            value={currency}
            onChange={(e) => setFilters((current) => ({ ...current, currency: e.target.value }))}
          >
            {options.currencies.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
        ) : null}
      </div>
    </header>
  );
  if (error)
    return (
      <section className="crm-dashboard__finance" aria-label="Dashboard overview">
        {heading}
        <div className={`crm-dashboard__overview-grid${workSummary ? '' : ' is-single'}`}>
          {workSummary}
          <section
            className="crm-dashboard__panel crm-dashboard__money-overview"
            aria-label="Money overview"
          >
            <h2>Money overview</h2>
            <p className="crm-dashboard__warning" id={`${inputId}-error`} role="alert">
              {error}
            </p>
          </section>
        </div>
      </section>
    );
  return (
    <section className="crm-dashboard__finance" aria-label="Dashboard overview">
      {heading}
      {data.missing ? (
        <p className="crm-dashboard__warning" role="status">
          Financial records unavailable for {data.missing} {data.missing === 1 ? 'shoot' : 'shoots'}
          . The chart and packages show available records only.
        </p>
      ) : null}
      <div className={`crm-dashboard__overview-grid${workSummary ? '' : ' is-single'}`}>
        {workSummary}
        {moneySummary}
      </div>
      <div className="crm-dashboard__finance-grid">
        <section className="crm-dashboard__panel" aria-label="Monthly income">
          <div className="crm-dashboard__section-heading">
            <h3>Income by month</h3>
          </div>
          <p className="crm-dashboard__chart-summary" aria-live="polite">
            {period}
            {month ? ' · selected month' : ''}
          </p>
          <div
            ref={chartScroll}
            className="crm-dashboard__chart-scroll"
            tabIndex={0}
            role="group"
            aria-label="Scrollable monthly income chart"
          >
            <div
              className="crm-dashboard__chart"
              role="group"
              aria-label={`Monthly received payments in ${currency}`}
              style={{ '--month-count': data.months.length } as React.CSSProperties}
            >
              {data.months.map((item, index) => (
                <button
                  key={item.key}
                  className="crm-dashboard__month"
                  aria-label={`${item.label}: ${formatCrmMoney(item.income, currency)}`}
                  aria-pressed={month === item.key}
                  data-current={item.key === today.slice(0, 7) || undefined}
                  onClick={() => setMonth(item.key)}
                >
                  <span className="crm-dashboard__bar-track">
                    <span
                      className="crm-dashboard__bar"
                      style={{
                        height: `${item.income ? Math.max(2, (item.income / max) * 100) : 0}%`,
                      }}
                    >
                      <span className="crm-dashboard__bar-value">
                        {formatCrmMoney(item.income, currency)}
                      </span>
                    </span>
                  </span>
                  <span className="crm-dashboard__month-label">
                    {MONTHS[Number(item.key.slice(5, 7)) - 1]}
                    <small>
                      {index === 0 || item.key.endsWith('-01') ? item.key.slice(0, 4) : ' '}
                    </small>
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="crm-dashboard__chart-footer">
            <span>
              {data.missing ? 'Available records' : month ? monthLabel(month) : 'Selected period'} ·
              received
            </span>
            <button onClick={() => setDetail({ title: 'Received payments', kind: 'Income' })}>
              {data.missing && !data.records.length ? '—' : formatCrmMoney(data.income, currency)}
              <ArrowUpRight size={14} aria-hidden="true" />
            </button>
          </div>
        </section>
        <div className="crm-dashboard__rankings">
          <section className="crm-dashboard__panel" aria-label="Income by package">
            <div className="crm-dashboard__section-heading">
              <h3>By package</h3>
            </div>
            <p className="crm-dashboard__ranking-caption">
              {data.missing ? 'Available payments' : 'Share of received payments'}
            </p>
            {data.packages.length ? (
              <div className="crm-dashboard__packages">
                {data.packages.map((p) => (
                  <button
                    key={p.key}
                    className="crm-dashboard__package"
                    onClick={() => setDetail({ title: p.title, kind: 'Income', packageKey: p.key })}
                  >
                    <span className="crm-dashboard__package-title">
                      {p.title}
                      <strong>{formatCrmMoney(p.received, currency)}</strong>
                    </span>
                    <span className="crm-dashboard__package-meta">
                      {p.count} {p.count === 1 ? 'shoot' : 'shoots'}
                      <span>{Math.round(p.share * 100)}%</span>
                    </span>
                    <span className="crm-dashboard__share">
                      <span style={{ width: `${p.share * 100}%` }} />
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="crm-dashboard__empty">No income recorded for this period.</p>
            )}
          </section>
          <section className="crm-dashboard__panel" aria-label="Top clients">
            <div className="crm-dashboard__section-heading">
              <h3>Top clients</h3>
              <span>Top 5</span>
            </div>
            <p className="crm-dashboard__ranking-caption">
              {data.missing ? 'Available payments' : 'By received payments'}
            </p>
            {data.clients.length ? (
              <ul className="crm-dashboard__client-ranking">
                {data.clients.slice(0, 5).map((client) => (
                  <li key={client.id}>
                    <Link to={demo ? '#dashboard' : `/crm/clients/${client.id}`}>
                      {demo ? (
                        <span
                          className="client-avatar crm-dashboard__client-avatar"
                          aria-hidden="true"
                        >
                          {clientInitials(client.name)}
                        </span>
                      ) : (
                        <ClientAvatar
                          id={client.id}
                          name={client.name}
                          className="crm-dashboard__client-avatar"
                        />
                      )}
                      <span className="crm-dashboard__rank-client">
                        <span>{client.name}</span>
                      </span>
                      <strong>{formatCrmMoney(client.received, currency)}</strong>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="crm-dashboard__empty">
                {data.missing
                  ? 'No client payments in available records.'
                  : 'No client payments for this period.'}
              </p>
            )}
          </section>
        </div>
      </div>
      <Dialog.Root
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      >
        <Dialog.Portal container={portal}>
          <Dialog.Overlay className="dui-dialog-overlay" />
          <CompactDialogContent
            title={detail?.title ?? 'Payments'}
            description={`${modalPeriod} · ${currency}`}
            onClose={() => setDetail(null)}
            closeLabel="Close financial details"
            className="crm-dashboard__details"
          >
            {data.missing ? (
              <p role="status" className="crm-dashboard__warning">
                Some financial records are unavailable. This list may be incomplete.
              </p>
            ) : null}
            {records.length ? (
              <ul className="crm-dashboard__records">
                {records.map(({ shoot, transaction: t }) => (
                  <li key={`${shoot.id}:${t.id}`}>
                    <Link
                      to={demo ? '#dashboard' : `/crm/shoots/${shoot.id}`}
                      onClick={(event) => {
                        if (
                          !event.metaKey &&
                          !event.ctrlKey &&
                          !event.shiftKey &&
                          !event.altKey &&
                          event.button === 0
                        )
                          setDetail(null);
                      }}
                    >
                      <span>
                        {shoot.title}
                        <small>
                          {cashDate(t.date)} · {t.kind}
                          {t.description ? ` · ${t.description}` : ''}
                        </small>
                      </span>
                      <strong data-negative={t.kind === 'Expense' || undefined}>
                        {t.kind === 'Expense' ? '−' : ''}
                        {formatCrmMoney(t.amountMinor, currency)}
                      </strong>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="crm-dashboard__empty">
                {data.missing
                  ? 'No available transactions for this period.'
                  : 'No transactions recorded for this period.'}
              </p>
            )}
          </CompactDialogContent>
        </Dialog.Portal>
      </Dialog.Root>
    </section>
  );
}
